import {
    Avatar,
    Box,
    Tooltip as ComponentsTooltip,
    IconButton,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import style from "./navigation-panel-footer.scss";
import AssetExportNotificationDetails from "components/asset-management-system/notification-details/AssetExportNotificationDetails";
import AssetImportNotificationDetails from "components/asset-management-system/notification-details/AssetImportNotificationDetails";
import AssetRegistrationNotificationDetails from "components/asset-management-system/notification-details/AssetRegistrationNotificationDetails";
import AuditLogDownloadDialog from "components/audit-log/AuditLogDownloadDialog";
import DarkModeSwitch from "components/dark-mode-switch/DarkModeSwitch";
import headerStyle from "components/header/header.scss";
import ToggleablePanel from "components/header/ToggleablePanel";
import Bell from "components/icons/Bell";
import Hourglass from "components/icons/Hourglass";
import RefreshButtonIcon from "components/icons/RefreshButtonIcon";
import { LicenseAlertData } from "components/licenses/license-alerts/manage-license-alerts/ManageLicenseAlertView";
import { ViewLicenseAlert } from "components/licenses/license-alerts/ViewLicenseAlert";
import LicenseEventExportDownloadDialog from "components/licenses/license-history/LicenseEventExportDownloadDialog";
import Modal from "components/modal/Modal";
import { useIsRouteEnabled } from "components/navigation/hooks/useIsRouteEnabled";
import {
    MainMenuItem,
    useMainMenuItems,
} from "components/navigation/navigation-panel/main-navigation-panel/navigation-icons-menu/useMainMenuItems";
import NotificationMenu from "components/notifications/NotificationMenu";
import ReportDeletionNotificationDetails from "components/reports/deletion/ReportDeletionNotificationDetails";
import ReportImportNotificationDetails from "components/reports/import/ReportImportNotificationDetails";
import NotificationDetails from "components/reports/notification-details/NotificationDetails";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import Tooltip from "components/tooltip/Tooltip";
import { UserMenu } from "components/user-menu/UserMenu";
import { getAssetExportTitleKey } from "domain/assetExport";
import { getAssetImportTitleKey } from "domain/assetImport";
import { getAssetRegistrationTitleKey } from "domain/assetRegistration";
import { AUTH_LICENSE_VIEW } from "domain/authority";
import { Notification } from "domain/notification";
import { Alert, LicenseAlertResult, licenseAlertsService } from "services/licenses/LicenseAlertService";
import { licensePoolService } from "services/licenses/LicensePoolService";
import { LicenseResponse, licenseService } from "services/licenses/LicenseService";
import { notificationService } from "services/notification/NotificationService";
import { ReportDeletionNotificationData } from "services/report/ReportDeletionService";
import { ImportNotificationData } from "services/report/ReportImportService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { getCurrentTenantDetails, getTenantName, hasTenantCookie } from "services/tenants/tenantCookieService";
import { tenantService } from "services/tenants/TenantService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { getInitials, isStringNotBlank } from "utils/commonFunctions";
import { logger } from "utils/logging";
import { useOutsideClick } from "utils/useOutsideClick";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    isMinimized?: boolean;
    className?: string;
    isOnBam: boolean;
    bmpSupportMenuItem?: MainMenuItem;
    onMenuItemSelect?: (item: MainMenuItem) => void;
    onSubNavigationOpenChange?: (value: boolean) => void;
}

const RETRY_FETCH_NOTIFICATIONS = 15_000;

function isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === "AbortError";
}

export const NavigationPanelFooter = (props: Props) => {
    const [reportImportDialogRefreshCount, setReportImportDialogRefreshCount] = React.useState<number>(0);
    const [importNotificationData, setImportNotificationData] = React.useState<ImportNotificationData>({
        jobId: null,
        jobType: "NORMAL",
    });
    const [reportExportDialogRefreshCount, setReportExportDialogRefreshCount] = React.useState<number>(0);
    const [reportDialogRefreshIconVisibility, setReportDialogRefreshIconVisibility] = React.useState<boolean>(false);
    const [reportExportJobId, setReportExportJobId] = React.useState<string>("");
    const [assetExportDialogRefreshCount, setAssetExportDialogRefreshCount] = React.useState<number>(0);
    const [assetExportDialogRefreshIconVisibility, setAssetExportDialogRefreshIconVisibility] =
        React.useState<boolean>(false);
    const [assetExportJobId, setAssetExportJobId] = React.useState<string>("");
    const [assetRegisterDialogRefreshCount, setAssetRegisterDialogRefreshCount] = React.useState<number>(0);
    const [assetRegisterDialogRefreshIconVisibility, setAssetRegisterDialogRefreshIconVisibility] =
        React.useState<boolean>(false);
    const [assetRegisterJobId, setAssetRegisterJobId] = React.useState<string>("");
    const [assetImportNotification, setAssetImportNotification] = React.useState<Notification | undefined>();
    const [auditLogExportData, setAuditLogExportData] = React.useState("");
    const [licenseExportNotification, setLicenseExportNotification] = React.useState<Notification | undefined>();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [notificationVisibility, setNotificationVisibility] = React.useState(false);
    const [notifications, setNotifications] = React.useState<Notification[]>([]);
    const [showBadge, setShowBadge] = React.useState(false);
    const [initialLoading, setInitialLoading] = React.useState(true);
    const [reportDeletionNotificationData, setReportDeleteNotificationData] =
        React.useState<ReportDeletionNotificationData>({
            jobId: null,
            totalReports: "",
        });

    const { t } = useTranslation();
    const theme = useTheme();
    const user = useSelector((state: StoreState) => state.userReducer.user);
    const [licenseAlertUuid, setLicenseAlertUuid] = React.useState<string>("");
    const [licenseAlertDetailsVisibility, setLicenseAlertDetailsVisibility] = React.useState<boolean>(false);
    const [failureMessage, setFailureMessage] = React.useState<string>("");
    const [licenseAlertDetails, setLicenseAlertDetails] = React.useState<Alert>({
        alertName: "",
        alertUuid: "",
        tenantUuid: "",
        tenantName: "",
        licenses: [],
        poolName: "",
        poolUuid: "",
    });

    const { activeMainMenuItem } = useMainMenuItems();
    const isRouteEnabled = useIsRouteEnabled();

    const hideLicenseAlert = () => {
        setLicenseAlertDetailsVisibility(false);
        setLicenseAlertDetails({
            alertName: "",
            alertUuid: "",
            tenantUuid: "",
            tenantName: "",
            licenses: [],
            poolName: "",
            poolUuid: "",
        });
        setLicenseAlertUuid("");
        setFailureMessage("");
    };
    const updateTenantDeletionStatus = useCallback(
        (updateTenantUuid: string, abortControllers: AbortController[]) => {
            const abortController = new AbortController();
            abortControllers.push(abortController);
            tenantService.updateTenantDeletionStatus(updateTenantUuid, abortController).catch((error) => {
                if (!isAbortError(error)) {
                    logger.error("Failed to update tenant deletion status.", error);
                }
            });
        },
        [abortControllers]
    );

    const fetchNotifications = useCallback(() => {
        const abortController: AbortController = new AbortController();
        abortControllers.push(abortController);
        notificationService
            .fetchNotifications(abortController)
            .then((data) => {
                setNotifications(data.sort((a, b) => b.creationDate.localeCompare(a.creationDate)));
            })
            .catch((error) => {
                if (!isAbortError(error)) {
                    logger.error("Failed to fetch notifications.", error);
                }
            });
    }, [abortControllers]);

    const closeReportDeletionResultsDialog = () =>
        setReportDeleteNotificationData((prevState: ReportDeletionNotificationData) => ({
            ...prevState,
            jobId: null,
        }));

    const closeReportImportResultsDialog = () =>
        setImportNotificationData((prevState: ImportNotificationData) => ({
            ...prevState,
            jobId: null,
            totalReports: "",
        }));

    const createNotificationBadge = (): JSX.Element | null => {
        const seenUuids = notificationService.getSeenNotifications() ?? [];
        let count = 0;
        for (const index in notifications) {
            const uuid = notifications[index].uuid;
            if (!seenUuids.includes(uuid)) {
                count++;
            }
        }
        if (count == 0) {
            return null;
        }

        return (
            <div
                className={classNames(
                    headerStyle.circle,
                    { [headerStyle.oneDigitSize]: count < 10 },
                    { [headerStyle.twoDigitsSize]: count > 9 && count < 100 },
                    { [headerStyle.threeDigitsSize]: count > 99 }
                )}
            >
                <div className={headerStyle.count}>{count < 100 ? count : t("Notification.count")}</div>
            </div>
        );
    };

    const handleFetch = () => {
        fetchNotifications();
    };

    const handleClickOutside = () => {
        setNotificationVisibility(false);
    };

    const ref = useOutsideClick(handleClickOutside);

    const getTitle = (notificationType: string) => {
        switch (notificationType) {
            case "REPORT_EXPORT_STARTED":
                return t("Notification.reportNotifications.inProgressTitle");
            case "REPORT_EXPORT_FAILED":
                return t("Notification.reportNotifications.failureTitle");
            case "REPORT_EXPORT_SUCCEEDED":
                return t("Notification.reportNotifications.successTitle");
            case "REPORT_EXPORT_WARNING":
                return t("Notification.reportNotifications.successWithFailTitle");
            default:
                return "";
        }
    };

    const viewLicenseAlert = React.useMemo(
        () => (
            <Modal
                key={1}
                isOpen={licenseAlertDetailsVisibility}
                hideModal={hideLicenseAlert}
                modalTitle={
                    licenseAlertDetails.alertName
                        ? t("LicenseAlerts.viewAlert", { alertName: licenseAlertDetails.alertName })
                        : ""
                }
            >
                {failureMessage !== "" ? (
                    t("LicenseAlerts.requestFailed")
                ) : (
                    <ViewLicenseAlert
                        licenseAlertDetails={licenseAlertDetails}
                        handleOkClick={hideLicenseAlert}
                        showForm={setLicenseAlertDetailsVisibility}
                    />
                )}
            </Modal>
        ),
        [licenseAlertDetailsVisibility]
    );

    const reportDeletionResult = React.useMemo(
        () => (
            <Modal
                isOpen={reportDeletionNotificationData.jobId !== null}
                hideModal={() =>
                    setReportDeleteNotificationData((prevState: ReportDeletionNotificationData) => ({
                        ...prevState,
                        jobId: null,
                    }))
                }
            >
                <ReportDeletionNotificationDetails
                    data={reportDeletionNotificationData}
                    onClose={closeReportDeletionResultsDialog}
                />
            </Modal>
        ),
        [reportDeletionNotificationData.jobId]
    );

    const setRefreshIconVisibility = (show: boolean) => setReportDialogRefreshIconVisibility(show);
    const notificationData = notifications.filter((each) => each.data.includes(reportExportJobId))[0];
    const assetExportNotificationData = isStringNotBlank(assetExportJobId)
        ? notifications.filter((each) => each.data.includes(assetExportJobId))[0]
        : undefined;
    const assetRegisterNotificationData = isStringNotBlank(assetRegisterJobId)
        ? notifications.filter((each) => each.data.includes(assetRegisterJobId))[0]
        : undefined;

    const reportImportResult = React.useMemo(
        () => (
            <Modal
                isOpen={importNotificationData.jobId !== null}
                hideModal={() =>
                    setImportNotificationData((prevState: ImportNotificationData) => ({ ...prevState, jobId: null }))
                }
                modalTitle={t("Notification.reportImportResultDialog.title")}
                action={
                    reportDialogRefreshIconVisibility && (
                        <Tooltip content={t("Notification.reportNotifications.refreshTableIconTooltip")}>
                            <span
                                className={headerStyle.reportImportResultRefreshIcon}
                                onClick={() => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.REPORT_IMPORT,
                                        action: Action.REFRESH,
                                    });
                                    setReportImportDialogRefreshCount((prev) => prev + 1);
                                }}
                            >
                                <span data-testid={testIds.workArea.report.reportResultsDialog.refreshButton}>
                                    <RefreshButtonIcon color={theme.iconFillColor} />
                                </span>
                            </span>
                        </Tooltip>
                    )
                }
            >
                <ReportImportNotificationDetails
                    data={importNotificationData}
                    refreshCount={reportImportDialogRefreshCount}
                    setRefreshIcon={setReportDialogRefreshIconVisibility}
                    onClose={closeReportImportResultsDialog}
                />
            </Modal>
        ),
        [importNotificationData.jobId, reportDialogRefreshIconVisibility, reportImportDialogRefreshCount]
    );

    let username;
    let usernameFormattedForInitials;
    if (getTenantName() != "") {
        username = getTenantName();
    } else {
        username = user ? (user.name ? user.name : user.username.split("@")[0]) : "";
        usernameFormattedForInitials = username.replace(".", " ");
    }
    React.useEffect(() => {
        if (licenseAlertUuid.length > 0) {
            fetchLicenseData(licenseAlertUuid);
        }
    }, [licenseAlertUuid]);
    React.useEffect(() => {
        if (initialLoading) {
            handleFetch();
            setInitialLoading(false);
            setShowBadge(true);
        }
        const notificationTimer = setTimeout(() => {
            handleFetch();
            setShowBadge(true);
        }, RETRY_FETCH_NOTIFICATIONS);

        notifications.forEach((notification) => {
            if (notification.type === "TENANT_DELETION_FAILED") {
                updateTenantDeletionStatus(JSON.parse(notification.data).tenant_uuid, abortControllers);
            }
        });

        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
            clearTimeout(notificationTimer);
        };
    }, [notifications]);

    const fetchLicenseData = (alertUuid: string) => {
        const abortController = new AbortController();
        const own = !userSessionService.userHasAllAuthorities([AUTH_LICENSE_VIEW]);
        licenseAlertsService
            .fetchLicenseAlertByUuid(alertUuid, abortController)
            .then((result: LicenseAlertResult) => {
                result.alert.poolUuid !== null
                    ? licensePoolService
                          .fetchPoolSpecificLicenses(abortController, result.alert.tenantUuid, result.alert.poolUuid)
                          .then((data) => {
                              const tempTenantsLicenses = data.allLicenses;
                              const processedLicenses: LicenseAlertData[] = result.alert.licenses.map((license) => {
                                  const matchingLicenseData = tempTenantsLicenses.find(
                                      (licenseData) => licenseData.licenseId === license.type
                                  );
                                  return {
                                      amountThreshold: license.amountThreshold,
                                      expirationThreshold: license.expirationThreshold,
                                      triggered: license.triggered,
                                      expirationDate: matchingLicenseData ? matchingLicenseData.expirationDate : "",
                                      remainingLicenses: matchingLicenseData?.amount,
                                      type: license.type,
                                      daysInputVisibility: true,
                                      amountInputVisibility: true,
                                  };
                              });
                              setLicenseAlertDetails({
                                  alertName: result.alert.alertName,
                                  alertUuid: result.alert.alertUuid,
                                  tenantUuid: result.alert.tenantUuid,
                                  tenantName: result.alert.tenantName,
                                  licenses: processedLicenses,
                                  poolName: "",
                                  poolUuid: result.alert.poolUuid,
                              });
                              setLicenseAlertDetailsVisibility(true);
                          })
                          .catch(() => {
                              setFailureMessage(t("LicenseAlerts.requestFailed"));
                              setLicenseAlertDetailsVisibility(true);
                          })
                    : licenseService
                          .fetchLicenses(
                              abortController,
                              undefined,
                              own,
                              result.alert.tenantUuid == userSessionService.currentUserDetails()?.tenantUuid
                                  ? ""
                                  : result.alert.tenantUuid,
                              ""
                          )
                          .then((data: LicenseResponse) => {
                              const tempTenantsLicenses = data.licenses;
                              const processedLicenses: LicenseAlertData[] = result.alert.licenses.map((license) => {
                                  const matchingLicenseData = tempTenantsLicenses.find(
                                      (licenseData) => licenseData.type === license.type
                                  );
                                  return {
                                      amountThreshold: license.amountThreshold,
                                      expirationThreshold: license.expirationThreshold,
                                      triggered: license.triggered,
                                      expirationDate: matchingLicenseData ? matchingLicenseData.expirationDate : "",
                                      remainingLicenses: matchingLicenseData
                                          ? own
                                              ? matchingLicenseData.available
                                              : matchingLicenseData.overallRemaining
                                          : 0,
                                      type: license.type,
                                      daysInputVisibility: true,
                                      amountInputVisibility: true,
                                  };
                              });
                              setLicenseAlertDetails({
                                  alertName: result.alert.alertName,
                                  alertUuid: result.alert.alertUuid,
                                  tenantUuid: result.alert.tenantUuid,
                                  tenantName: result.alert.tenantName,
                                  licenses: processedLicenses,
                                  poolName: result.alert.poolName,
                                  poolUuid: result.alert.poolUuid,
                              });
                              setLicenseAlertDetailsVisibility(true);
                          })
                          .catch(() => {
                              setLicenseAlertDetailsVisibility(true);
                              setFailureMessage("error");
                          });
            })
            .catch(() => {
                setFailureMessage("error");
                setLicenseAlertDetailsVisibility(true);
            });
    };

    /* TODO BCC-4838 make into a global function */
    const calculateTrialDaysLeft = () => {
        let tenantExpirationDate = user?.tenantExpirationDate;

        if (hasTenantCookie()) {
            tenantExpirationDate = getCurrentTenantDetails().expirationDate;
        }

        if (!tenantExpirationDate) {
            return null;
        }

        const trialEndDate = new Date(tenantExpirationDate);
        const today = new Date();

        trialEndDate.setUTCHours(19, 59, 59, 999);
        today.setUTCHours(19, 59, 59, 999);

        const calculateDifference = trialEndDate.getTime() - today.getTime();

        const daysLeft = Math.ceil(calculateDifference / (1000 * 60 * 60 * 24));

        return daysLeft;
    };

    const ExpirationDateFooter = () => {
        const trialDaysLeft = calculateTrialDaysLeft();
        if (!trialDaysLeft || getCurrentTenantDetails().contractType === "PRODUCTION") {
            return null;
        }

        return (
            <>
                <Box columnLayout paddingBottom="Sm">
                    <ComponentsTooltip>
                        <TooltipContent testId={testIds.navigation.expirationDateStatusBadge.badgeTooltip}>
                            {trialDaysLeft > 0
                                ? t("CustomerProfile.trialTenantsNavPanel.trialDaysLeft", {
                                      days: trialDaysLeft,
                                  })
                                : t("CustomerProfile.trialTenantsNavPanel.trialEndedText")}
                        </TooltipContent>
                        <TooltipTrigger
                            testId={testIds.navigation.expirationDateStatusBadge.trigger}
                            className={style.hourglassIconTooltipTrigger}
                        >
                            {trialDaysLeft > 0
                                ? displayStatusBadge(Status.WARNING, "", theme.warningBadgeTextColor)
                                : displayStatusBadge(Status.NEUTRAL, "", theme.neutralBadgeTextColor)}
                        </TooltipTrigger>
                    </ComponentsTooltip>
                </Box>
            </>
        );
    };

    function displayStatusBadge(status: Status, statusText: string, iconColor: string) {
        return (
            <StatusBadge
                icon={<Hourglass className={style.hourglassIcon} color={iconColor} />}
                values={[
                    {
                        status: status,
                        title: statusText,
                    },
                ]}
            />
        );
    }

    const createSupportButton = (item: MainMenuItem) => {
        if (!isRouteEnabled(item.mainRoute)) {
            return null;
        }

        const isActive = item.mainRoute.path === activeMainMenuItem?.mainRoute.path;

        return (
            <ComponentsTooltip>
                <TooltipContent testId={testIds.navigation.support.tooltip}>{t(item.title)}</TooltipContent>
                <TooltipTrigger testId={testIds.navigation.support.trigger}>
                    <IconButton
                        onClick={() => {
                            props.onMenuItemSelect?.(item);
                        }}
                        testId={item.testId}
                        aria-label={t(item.title)}
                        className={classNames(style.supportLink, { [style.active]: isActive })}
                    >
                        {item.icon}
                    </IconButton>
                </TooltipTrigger>
            </ComponentsTooltip>
        );
    };

    return (
        <>
            <Box
                verticalAlignment="Center"
                paddingBottom="Md"
                paddingTop="Md"
                columnLayout
                className={style.navigationPanelFooter}
            >
                <ExpirationDateFooter />
                {props.bmpSupportMenuItem && (
                    <Box verticalAlignment="Center">{createSupportButton(props.bmpSupportMenuItem)}</Box>
                )}

                <Box verticalAlignment="Center" className={style.darkModeButtonContainer}>
                    <DarkModeSwitch />
                </Box>

                {!hasTenantCookie() && (
                    <Box
                        verticalAlignment="Center"
                        className={style.notificationButtonContainer}
                        ref={ref}
                        onBlur={(event) => {
                            if (event.relatedTarget == null) {
                                return;
                            }
                            if (!event.currentTarget.contains(event.relatedTarget)) {
                                setNotificationVisibility(false);
                            }
                        }}
                        onKeyDown={(event) => {
                            if (notificationVisibility && event.key === "Escape") {
                                setNotificationVisibility(false);
                            }
                        }}
                    >
                        <ComponentsTooltip placement="top">
                            <TooltipContent testId={testIds.navigation.notificationMenu.tooltip}>
                                {t("AltText.notification")}
                            </TooltipContent>
                            <TooltipTrigger testId={testIds.navigation.notificationMenu.trigger}>
                                <IconButton
                                    testId={testIds.navigation.notificationMenu.button}
                                    onClick={() => {
                                        if (notificationVisibility) {
                                            setNotificationVisibility(false);
                                        } else {
                                            setNotificationVisibility(true);
                                            notificationService.setSeenNotifications(notifications);
                                        }
                                    }}
                                    className={style.notificationButton}
                                >
                                    {showBadge && notifications.length > 0 ? createNotificationBadge() : null}
                                    <Bell fillColor={"var(--app-navigationPanel-iconColor)"} />
                                </IconButton>
                            </TooltipTrigger>
                        </ComponentsTooltip>
                        {notificationVisibility && (
                            <NotificationMenu
                                onDelete={setNotifications}
                                notifications={notifications}
                                showImportReportDialog={setImportNotificationData}
                                showExportReport={setReportExportJobId}
                                showExportAsset={setAssetExportJobId}
                                showImportAsset={setAssetImportNotification}
                                showRegisterAsset={setAssetRegisterJobId}
                                showExportAuditLog={setAuditLogExportData}
                                showExportLicenseEvents={setLicenseExportNotification}
                                showDeleteReportDialog={setReportDeleteNotificationData}
                                showLicenseAlertDialog={setLicenseAlertUuid}
                                className={style.notificationMenu}
                            />
                        )}
                    </Box>
                )}

                <ToggleablePanel
                    title={username}
                    testId={testIds.navigation.userMenu.button}
                    menuType={"HEADER"}
                    icon={
                        <Avatar
                            backgroundColor={"#e3e4b4"}
                            textColor={"var(--app-navigationPanel-userNameIconLettersColor)"}
                            size={"Lg"}
                            value={getInitials(usernameFormattedForInitials ? usernameFormattedForInitials : username)}
                            box
                        />
                    }
                    buttonClass={style.userName}
                    hideChevron
                >
                    <UserMenu className={style.userMenu} onSubNavigationOpenChange={props.onSubNavigationOpenChange} />
                </ToggleablePanel>
            </Box>

            {reportImportResult}
            {reportDeletionResult}
            {viewLicenseAlert}
            <Modal
                isOpen={reportExportJobId !== ""}
                hideModal={() => setReportExportJobId("")}
                modalTitle={getTitle(notificationData?.type)}
                action={
                    reportDialogRefreshIconVisibility && (
                        <Tooltip content={t("Notification.reportNotifications.refreshTableIconTooltip")}>
                            <span
                                className={headerStyle.reportExportResultRefreshIcon}
                                onClick={() => {
                                    setReportExportDialogRefreshCount((prev) => prev + 1);
                                }}
                            >
                                <RefreshButtonIcon color={theme.iconFillColor} />
                            </span>
                        </Tooltip>
                    )
                }
            >
                <div className={headerStyle.fixedWidthModal}>
                    {reportExportJobId && (
                        <NotificationDetails
                            refreshCount={reportExportDialogRefreshCount}
                            jobId={reportExportJobId}
                            setRefreshIcon={setRefreshIconVisibility}
                            jobDetail={notificationData}
                        />
                    )}
                </div>
            </Modal>
            <Modal
                isOpen={assetExportJobId !== ""}
                hideModal={() => setAssetExportJobId("")}
                modalTitle={getAssetExportTitleKey(
                    assetExportNotificationData?.type,
                    assetExportNotificationData?.data,
                    t
                )}
                action={
                    assetExportDialogRefreshIconVisibility && (
                        <Tooltip content={t("Notification.assetExportNotifications.refreshTableIconTooltip")}>
                            <span
                                className={headerStyle.reportExportResultRefreshIcon}
                                onClick={() => {
                                    setAssetExportDialogRefreshCount((prev) => prev + 1);
                                }}
                            >
                                <RefreshButtonIcon color={theme.iconFillColor} />
                            </span>
                        </Tooltip>
                    )
                }
            >
                <div className={headerStyle.fixedWidthModal}>
                    {assetExportJobId && (
                        <AssetExportNotificationDetails
                            refreshCount={assetExportDialogRefreshCount}
                            setRefreshIcon={setAssetExportDialogRefreshIconVisibility}
                            jobDetail={assetExportNotificationData}
                        />
                    )}
                </div>
            </Modal>
            <Modal
                isOpen={assetRegisterJobId !== ""}
                hideModal={() => setAssetRegisterJobId("")}
                modalTitle={getAssetRegistrationTitleKey(assetRegisterNotificationData?.type, t)}
                action={
                    assetRegisterDialogRefreshIconVisibility && (
                        <Box onClick={() => setAssetRegisterDialogRefreshCount((prev) => prev + 1)}>
                            <ComponentsTooltip placement="top">
                                <TooltipContent
                                    testId={testIds.workArea.asset.assetRegistrationResultsDialog.refreshButtonTooltip}
                                >
                                    {t("Notification.assetRegistrationNotifications.refreshTableIconTooltip")}
                                </TooltipContent>
                                <TooltipTrigger
                                    testId={testIds.workArea.asset.assetRegistrationResultsDialog.refreshButton}
                                >
                                    <RefreshButtonIcon color={theme.iconFillColor} />
                                </TooltipTrigger>
                            </ComponentsTooltip>
                        </Box>
                    )
                }
            >
                <div className={headerStyle.fixedWidthModal}>
                    {assetRegisterJobId && (
                        <AssetRegistrationNotificationDetails
                            refreshCount={assetRegisterDialogRefreshCount}
                            setRefreshIcon={setAssetRegisterDialogRefreshIconVisibility}
                            jobDetail={assetRegisterNotificationData}
                        />
                    )}
                </div>
            </Modal>
            <Modal
                isOpen={auditLogExportData !== ""}
                hideModal={() => setAuditLogExportData("")}
                modalTitle={t("AuditLogs.export.downloadTitle")}
            >
                {auditLogExportData && <AuditLogDownloadDialog notificationData={auditLogExportData} />}
            </Modal>
            <Modal
                isOpen={assetImportNotification !== undefined}
                hideModal={() => setAssetImportNotification(undefined)}
                modalTitle={getAssetImportTitleKey(assetImportNotification?.type, t)}
            >
                <div className={headerStyle.fixedWidthModal}>
                    {assetImportNotification && (
                        <AssetImportNotificationDetails notification={assetImportNotification} />
                    )}
                </div>
            </Modal>
            <Modal
                isOpen={licenseExportNotification !== undefined}
                hideModal={() => setLicenseExportNotification(undefined)}
                modalTitle={t("LicenseEvent.export.downloadTitle")}
            >
                {licenseExportNotification && (
                    <LicenseEventExportDownloadDialog notification={licenseExportNotification} />
                )}
            </Modal>
        </>
    );
};
